
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

type AdminContextType = {
  isAdmin: boolean | null;
  isLoading: boolean;
  refreshAdminStatus: () => Promise<boolean>;
};

const AdminContext = createContext<AdminContextType>({
  isAdmin: null,
  isLoading: true,
  refreshAdminStatus: async () => false,
});

export const useAdminContext = () => useContext(AdminContext);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { t } = useTranslation(['admin', 'common']);

  // Optimized check admin status with tiered caching strategy
  const checkAdminStatus = useCallback(async (forceCheck: boolean = false) => {
    try {
      // Skip check if user is not logged in
      if (!user) {
        setIsAdmin(false);
        setIsLoading(false);
        return false;
      }

      // Use memory state if available and not force checking
      if (!forceCheck && isAdmin !== null) {
        return isAdmin;
      }
      
      // First check if we have a cached result that's still valid
      const cachedAdminStatus = localStorage.getItem('adminCheckPerformed');
      const lastCheckedTimestamp = localStorage.getItem('adminCheckTimestamp');
      
      // If we have a cached result and it's less than 30 minutes old and we're not forcing a check
      if (!forceCheck && cachedAdminStatus === 'true' && lastCheckedTimestamp) {
        const thirtyMinutesAgo = new Date().getTime() - (30 * 60 * 1000);
        if (parseInt(lastCheckedTimestamp) > thirtyMinutesAgo) {
          console.log('Using cached admin status - valid for 30 minutes');
          setIsAdmin(true);
          setIsLoading(false);
          return true;
        }
      }
      
      setIsLoading(true);
      
      // Check if user has admin privileges directly from the database
      const { data, error } = await supabase
        .from('customer_profiles')
        .select('tier')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        setIsAdmin(false);
        setIsLoading(false);
        return false;
      }

      const userIsAdmin = data.tier === 'admin';
      setIsAdmin(userIsAdmin);
      
      // Update the cache if the user is an admin
      if (userIsAdmin) {
        localStorage.setItem('adminCheckPerformed', 'true');
        localStorage.setItem('adminCheckTimestamp', new Date().getTime().toString());
      } else {
        // Clear the cache if the user is not an admin anymore
        localStorage.removeItem('adminCheckPerformed');
        localStorage.removeItem('adminCheckTimestamp');
      }
      
      setIsLoading(false);
      return userIsAdmin;
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      setIsLoading(false);
      return false;
    }
  }, [user, isAdmin, t, toast]);

  // Initialize admin status on mount and auth changes
  useEffect(() => {
    checkAdminStatus();
    
    // Subscribe to auth changes to recheck admin status
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkAdminStatus(true); // Force check on auth state change
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [checkAdminStatus]);

  const refreshAdminStatus = () => {
    return checkAdminStatus(true);
  };

  return (
    <AdminContext.Provider
      value={{
        isAdmin,
        isLoading,
        refreshAdminStatus,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};
